import React from 'react';
import { Badge, Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';

/** Normalize Firestore Timestamp or Date to Date. */
function toDate(value: Date | null | undefined | { toDate?: () => Date }): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return null;
}

interface ProfitPlus2k8SyncStatusProps {
  /** From entity.profitPlus2k8.syncedAt (may be Firestore Timestamp). */
  syncedAt: Date | null | undefined | { toDate?: () => Date };
  /** Product export: bridge error message when sync to Profit failed. */
  exportError?: string | null;
  /**
   * When true (e.g. ERP fields complete + org export mode), show retry for failed export.
   * Ignored for non-product usages (orders, clients, etc.).
   */
  eligibleForProfitExport?: boolean;
  onRetryProfitExport?: () => void;
  retryPending?: boolean;
  /** Polling product doc for sync result (shows checking state). */
  polling?: boolean;
  /** Optional class for the wrapper. */
  className?: string;
  /** Shown when `exportError` is set but `eligibleForProfitExport` is false (e.g. orders, not products). */
  ineligibleErrorHint?: string;
}

/**
 * Shows sync status for Profit Plus 2k8: "Sincronizado" + date, "No sincronizado",
 * or error + optional retry for products when export failed.
 */
const ProfitPlus2k8SyncStatus: React.FC<ProfitPlus2k8SyncStatusProps> = ({
  syncedAt,
  exportError,
  eligibleForProfitExport = false,
  onRetryProfitExport,
  retryPending = false,
  polling = false,
  className = '',
  ineligibleErrorHint,
}) => {
  const date = toDate(syncedAt);
  const synced = date != null;
  const err = (exportError ?? '').trim();
  const hasError = err.length > 0;

  const formatDate = (d: Date): string => {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  if (polling) {
    const tip = (
      <Tooltip id="pp2k8-polling">
        <div className="text-start small" style={{ maxWidth: 280 }}>
          Comprobando el estado en el servidor (varias lecturas espaciadas) hasta que el puente
          actualice la sincronización con Profit.
        </div>
      </Tooltip>
    );
    return (
      <OverlayTrigger placement="top" overlay={tip}>
        <div
          className={`d-flex flex-column gap-1 align-items-start ${className}`}
        >
          <Badge bg="info" text="white" className="text-nowrap d-flex align-items-center gap-1">
            <Spinner animation="border" size="sm" />
            Comprobando…
          </Badge>
          <span className="text-muted" style={{ fontSize: '0.7rem' }}>
            Sincronizando con profit..
          </span>
        </div>
      </OverlayTrigger>
    );
  }

  if (hasError && eligibleForProfitExport) {
    const tooltip = (
      <Tooltip id="pp2k8-export-err">
        <div className="text-start small" style={{ maxWidth: 280 }}>
          <strong>Error al exportar a Profit</strong>
          <div className="mt-1">{err}</div>
          <div className="mt-1 text-white-50">Pulsa Reintentar para volver a enviar al puente.</div>
        </div>
      </Tooltip>
    );
    return (
      <div className={`d-flex flex-column gap-1 align-items-start ${className}`}>
        <OverlayTrigger placement="top" overlay={tooltip}>
          <Badge bg="danger" text="white" className="text-nowrap">
            Error sync
          </Badge>
        </OverlayTrigger>
        {onRetryProfitExport && (
          <Button
            type="button"
            variant="outline-danger"
            size="sm"
            className="py-0 px-1 text-nowrap"
            disabled={retryPending}
            onClick={e => {
              e.stopPropagation();
              onRetryProfitExport();
            }}
          >
            {retryPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Enviando…
              </>
            ) : (
              'Reintentar'
            )}
          </Button>
        )}
      </div>
    );
  }

  if (hasError && !eligibleForProfitExport) {
    const hint =
      ineligibleErrorHint ||
      'Completa los campos ERP del producto y guarda para poder reintentar.';
    const tooltip = (
      <Tooltip id="pp2k8-export-err-partial">
        <div className="text-start small" style={{ maxWidth: 280 }}>
          <strong>Error al exportar</strong>
          <div className="mt-1">{err}</div>
          <div className="mt-1 text-white-50">{hint}</div>
        </div>
      </Tooltip>
    );
    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        <Badge bg="warning" text="dark" className={`text-nowrap ${className}`}>
          Error
        </Badge>
      </OverlayTrigger>
    );
  }

  const content = synced ? (
    <Badge bg="success" text="white" className="text-nowrap">
      {formatDate(date!)}
    </Badge>
  ) : (
    <Badge bg="secondary" text="white" className="text-nowrap">
      No sincronizado
    </Badge>
  );

  const tooltip = synced
    ? `Sincronizado con Profit Plus 2k8 el ${formatDate(date!)}`
    : 'No sincronizado con Profit Plus 2k8';

  const canSyncUnsynced =
    !synced &&
    !hasError &&
    eligibleForProfitExport &&
    Boolean(onRetryProfitExport);

  if (canSyncUnsynced) {
    return (
      <div className={`d-flex flex-column gap-1 align-items-start ${className}`}>
        <OverlayTrigger placement="top" overlay={<Tooltip id="pp2k8-sync">{tooltip}</Tooltip>}>
          <span>{content}</span>
        </OverlayTrigger>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          className="py-0 px-1 text-nowrap"
          disabled={retryPending}
          onClick={e => {
            e.stopPropagation();
            onRetryProfitExport?.();
          }}
        >
          {retryPending ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Enviando…
            </>
          ) : (
            'Sincronizar'
          )}
        </Button>
      </div>
    );
  }

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id="pp2k8-sync">{tooltip}</Tooltip>}>
      <span className={className}>{content}</span>
    </OverlayTrigger>
  );
};

export default ProfitPlus2k8SyncStatus;
